import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';
import { hikingCompleteProfilePath, accountPath } from '../utils/paths';
import {
  HIKER_EXPERIENCE_LEVELS,
  HIKER_GROUP_PREFERENCES,
  TRAIL_DIFFICULTIES,
  TRAIL_TERRAINS,
  formatDistance,
  formatHikerExperience,
  formatHikerGroupPreference,
  formatTrailDifficulty,
  formatTrailTerrain,
} from '../config/hikerProfileOptions';
import './PlayerRegistration.css';
import './HikingProfile.css';
import './AccountProfile.css';

const emptySportForm = {
  experienceLevel: 'BEGINNER',
  preferredDifficulty: '',
  comfortableDistanceKm: '',
  preferredTerrains: [],
  groupPreference: '',
  canCarpool: false,
  emergencyContactName: '',
  emergencyContactPhone: '',
  medicalNotes: '',
};

const HikingProfile = () => {
  const navigate = useNavigate();
  const { user, isAuthenticated } = useAuth();
  const [profile, setProfile] = useState(null);
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState(emptySportForm);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    if (!isAuthenticated || !user) {
      setLoading(false);
      return;
    }
    const load = async () => {
      try {
        setLoading(true);
        setError('');
        const [hikerData, accountData] = await Promise.all([
          api.getMyHikerProfile(user.id),
          api.getAccountProfile(user.id).catch(() => null),
        ]);
        setProfile(hikerData);
        setAccount(accountData);
        setFormData({
          experienceLevel: hikerData.experienceLevel || 'BEGINNER',
          preferredDifficulty: hikerData.preferredDifficulty || '',
          comfortableDistanceKm: hikerData.comfortableDistanceKm ?? '',
          preferredTerrains: hikerData.preferredTerrains || [],
          groupPreference: hikerData.groupPreference || '',
          canCarpool: hikerData.canCarpool === true,
          emergencyContactName: hikerData.emergencyContactName || '',
          emergencyContactPhone: hikerData.emergencyContactPhone || '',
          medicalNotes: hikerData.medicalNotes || '',
        });
      } catch {
        setProfile(null);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [isAuthenticated, user]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setError('');
    setSuccess('');
  };

  const toggleTerrain = (terrain) => {
    setFormData((prev) => {
      const selected = prev.preferredTerrains.includes(terrain)
        ? prev.preferredTerrains.filter((t) => t !== terrain)
        : [...prev.preferredTerrains, terrain];
      return { ...prev, preferredTerrains: selected };
    });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    setSuccess('');
    try {
      const updated = await api.saveHikerProfile(user.id, {
        experienceLevel: formData.experienceLevel,
        preferredDifficulty: formData.preferredDifficulty || null,
        comfortableDistanceKm: formData.comfortableDistanceKm
          ? parseFloat(formData.comfortableDistanceKm)
          : null,
        preferredTerrains: formData.preferredTerrains,
        groupPreference: formData.groupPreference || null,
        canCarpool: formData.canCarpool,
        emergencyContactName: formData.emergencyContactName || null,
        emergencyContactPhone: formData.emergencyContactPhone || null,
        medicalNotes: formData.medicalNotes || null,
      });
      setProfile(updated);
      setEditing(false);
      setSuccess('Hiking profile updated.');
    } catch (err) {
      setError(err.message || 'Failed to update profile');
    } finally {
      setSaving(false);
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="card hiking-profile-page">
        <p>
          <Link to="/login">Log in</Link> to view your hiking profile.
        </p>
      </div>
    );
  }

  if (loading) {
    return <div className="loading">Loading profile...</div>;
  }

  if (!profile) {
    return (
      <div className="card hiking-profile-page">
        <h2>Hiking profile</h2>
        <p>You have not set up a hiking profile yet.</p>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => navigate(hikingCompleteProfilePath())}
        >
          Complete hiking profile
        </button>
      </div>
    );
  }

  const displayName = account?.name || profile.name;
  const displayPhone = account?.phoneNumber || profile.phoneNumber;
  const displayPhoto = account?.profilePictureUrl || profile.profilePictureUrl;

  return (
    <div className="card hiking-profile-page">
      <div
        style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'flex-start',
          gap: '1rem',
          flexWrap: 'wrap',
        }}
      >
        <div>
          <h2>My hiking profile</h2>
          <p className="form-help">Experience, comfortable distance, and trail preferences.</p>
        </div>
        {!editing && (
          <button type="button" className="btn btn-secondary" onClick={() => setEditing(true)}>
            Edit hiking details
          </button>
        )}
      </div>

      <p className="account-profile-page__shared-note">
        Name, photo, and phone are managed on your <Link to={accountPath()}>account profile</Link>.
      </p>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      {!editing ? (
        <>
          {displayPhoto && (
            <div className="profile-picture-preview" style={{ marginBottom: '1rem' }}>
              <img
                src={
                  displayPhoto.startsWith('http')
                    ? displayPhoto
                    : `${process.env.REACT_APP_API_URL?.replace(/\/api\/?$/, '') || 'http://localhost:8080'}${displayPhoto}`
                }
                alt=""
              />
            </div>
          )}
          <dl className="hiking-profile-page__detail-grid">
            <div>
              <dt>Name</dt>
              <dd>{displayName}</dd>
            </div>
            <div>
              <dt>Phone</dt>
              <dd>{displayPhone || '—'}</dd>
            </div>
            <div>
              <dt>Experience</dt>
              <dd>{formatHikerExperience(profile.experienceLevel)}</dd>
            </div>
            <div>
              <dt>Preferred difficulty</dt>
              <dd>{formatTrailDifficulty(profile.preferredDifficulty)}</dd>
            </div>
            <div>
              <dt>Comfortable distance</dt>
              <dd>{formatDistance(profile.comfortableDistanceKm)}</dd>
            </div>
            <div>
              <dt>Group preference</dt>
              <dd>{formatHikerGroupPreference(profile.groupPreference)}</dd>
            </div>
            <div>
              <dt>Can carpool</dt>
              <dd>{profile.canCarpool ? 'Yes' : 'No'}</dd>
            </div>
            <div>
              <dt>Emergency contact</dt>
              <dd>
                {profile.emergencyContactName
                  ? `${profile.emergencyContactName}${profile.emergencyContactPhone ? ` · ${profile.emergencyContactPhone}` : ''}`
                  : '—'}
              </dd>
            </div>
          </dl>
          {profile.preferredTerrains?.length > 0 && (
            <>
              <h3>Preferred terrain</h3>
              <ul className="hiking-profile-page__terrain-list">
                {profile.preferredTerrains.map((t) => (
                  <li key={t}>{formatTrailTerrain(t)}</li>
                ))}
              </ul>
            </>
          )}
          {profile.medicalNotes && (
            <p style={{ marginTop: '1rem', color: '#55604f' }}>
              <strong>Private notes:</strong> {profile.medicalNotes}
            </p>
          )}
          <p style={{ marginTop: '1.5rem' }}>
            Also run? <Link to="/running/profile">Edit your running profile →</Link>
          </p>
        </>
      ) : (
        <form onSubmit={handleSave}>
          <div className="form-group">
            <label htmlFor="experienceLevel">Hiking experience *</label>
            <select
              id="experienceLevel"
              name="experienceLevel"
              value={formData.experienceLevel}
              onChange={handleChange}
              required
            >
              {HIKER_EXPERIENCE_LEVELS.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="preferredDifficulty">Preferred difficulty</label>
            <select
              id="preferredDifficulty"
              name="preferredDifficulty"
              value={formData.preferredDifficulty}
              onChange={handleChange}
            >
              <option value="">— Select —</option>
              {TRAIL_DIFFICULTIES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="comfortableDistanceKm">Comfortable distance (km)</label>
            <input
              type="number"
              id="comfortableDistanceKm"
              name="comfortableDistanceKm"
              min="1"
              max="80"
              step="0.5"
              value={formData.comfortableDistanceKm}
              onChange={handleChange}
            />
          </div>
          <div className="form-group">
            <label>Preferred terrain</label>
            <div className="hiking-profile-page__chips">
              {TRAIL_TERRAINS.map((opt) => (
                <button
                  key={opt.value}
                  type="button"
                  className={`hiking-profile-page__chip${formData.preferredTerrains.includes(opt.value) ? ' is-selected' : ''}`}
                  onClick={() => toggleTerrain(opt.value)}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>
          <div className="form-group">
            <label htmlFor="groupPreference">Group hike preference</label>
            <select
              id="groupPreference"
              name="groupPreference"
              value={formData.groupPreference}
              onChange={handleChange}
            >
              <option value="">— Select —</option>
              {HIKER_GROUP_PREFERENCES.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label htmlFor="canCarpool" className="hiking-profile-page__checkbox">
              <input
                type="checkbox"
                id="canCarpool"
                name="canCarpool"
                checked={formData.canCarpool}
                onChange={handleChange}
              />
              I can drive others to the trailhead
            </label>
          </div>
          <div className="form-group">
            <label htmlFor="emergencyContactName">Emergency contact name</label>
            <input
              type="text"
              id="emergencyContactName"
              name="emergencyContactName"
              value={formData.emergencyContactName}
              onChange={handleChange}
              maxLength={100}
            />
          </div>
          <div className="form-group">
            <label htmlFor="emergencyContactPhone">Emergency contact phone</label>
            <input
              type="tel"
              id="emergencyContactPhone"
              name="emergencyContactPhone"
              value={formData.emergencyContactPhone}
              onChange={handleChange}
              maxLength={30}
            />
          </div>
          <div className="form-group">
            <label htmlFor="medicalNotes">Medical notes (private)</label>
            <textarea
              id="medicalNotes"
              name="medicalNotes"
              value={formData.medicalNotes}
              onChange={handleChange}
              rows={3}
              maxLength={500}
            />
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
            <button type="submit" className="btn btn-primary" disabled={saving}>
              {saving ? 'Saving...' : 'Save changes'}
            </button>
            <button type="button" className="btn btn-secondary" onClick={() => setEditing(false)}>
              Cancel
            </button>
          </div>
        </form>
      )}
    </div>
  );
};

export default HikingProfile;
